import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errText, fireEvent } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen } from "lucide-react";

export function AuthPage({ mode }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const isReg = mode === "register";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (isReg) await register(email, password, name || "Friend");
      else await login(email, password);
      if (isReg) { try { if (localStorage.getItem("ipf_ref_anon")) { fireEvent("anon_recipient_account"); } else if (localStorage.getItem("ipf_ref")) { fireEvent("account_from_share"); } } catch {} }
      toast.success(isReg ? "Welcome!" : "Welcome back.");
      nav("/");
    } catch (e2) { setErr(errText(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="text-center mb-8">
        <BookOpen className="w-8 h-8 text-[#2C4C3B] mx-auto mb-3" strokeWidth={1.5} />
        <h1 className="font-serif-display text-3xl text-[#0F172A]">{isReg ? "Create your account" : "Welcome back"}</h1>
        <p className="text-[#475569] mt-2 text-sm">{isReg ? "Save Wisdom Paths, favorites, and a private journal." : "Sign in to your saved wisdom."}</p>
      </div>
      <form onSubmit={submit} className="space-y-4 bg-white border border-[#E2E8F0] rounded-2xl p-7">
        {err && <div data-testid="auth-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        {isReg && <Input label="Name" value={name} onChange={setName} testid="name-input" placeholder="Your name" />}
        <Input label="Email" type="email" value={email} onChange={setEmail} testid="email-input" placeholder="you@example.com" required />
        <Input label="Password" type="password" value={password} onChange={setPassword} testid="password-input" placeholder="••••••••" required />
        <button data-testid="auth-submit-btn" disabled={busy} className="w-full py-3 rounded-full bg-[#2C4C3B] text-white font-semibold hover:bg-[#1E3629] transition-colors disabled:opacity-60">
          {busy ? "Please wait…" : isReg ? "Create account" : "Log in"}
        </button>
        {!isReg && <Link to="/forgot-password" className="block text-center text-sm text-[#475569] hover:text-[#2C4C3B]">Forgot your password?</Link>}
      </form>
      <p className="text-center text-sm text-[#475569] mt-5">
        {isReg ? "Already have an account? " : "New here? "}
        <Link to={isReg ? "/login" : "/register"} className="text-[#2C4C3B] font-semibold">{isReg ? "Log in" : "Create an account"}</Link>
      </p>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch {} };
  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <h1 className="font-serif-display text-3xl text-[#0F172A] text-center mb-6">Reset your password</h1>
      {sent ? <p className="text-center text-[#475569] bg-white border border-[#E2E8F0] rounded-2xl p-7">If that email exists, a reset link has been created. (In this build the link is logged on the server.)</p> :
      <form onSubmit={submit} className="space-y-4 bg-white border border-[#E2E8F0] rounded-2xl p-7">
        <Input label="Email" type="email" value={email} onChange={setEmail} testid="forgot-email-input" required />
        <button data-testid="forgot-submit-btn" className="w-full py-3 rounded-full bg-[#2C4C3B] text-white font-semibold">Send reset link</button>
      </form>}
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testid, placeholder, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#475569] mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-[#E2E8F0] bg-white px-4 py-2.5 focus:outline-none focus:border-[#2C4C3B] transition-colors" />
    </div>
  );
}
